/**
 * Layout Parameter Logger
 *
 * Enable/disable logging by setting LAYOUT_LOGGER_ENABLED in the .env file:
 * - LAYOUT_LOGGER_ENABLED=true  -> Enable logging
 * - LAYOUT_LOGGER_ENABLED=false -> Disable logging
 * - Leave unset -> Log only in development mode (default)
 */
import { useStyles } from '@wix/tpa-settings/react';
import { useParamLogger } from './paramLogger';

interface LayoutParamsObject {
  [key: string]: any;
}

/**
 * Custom hook for logging all layout parameter changes in widget components
 *
 * Environment Control:
 * Add to your .env file:
 * ```
 * LAYOUT_LOGGER_ENABLED=true   # Enable logging
 * LAYOUT_LOGGER_ENABLED=false  # Disable logging
 * # Leave unset for development mode default
 * ```
 *
 * Usage:
 * ```typescript
 * import layoutParams from '../../layoutParams';
 * import { useLayoutParamLogger } from '../utils/layoutParamLogger';
 *
 * export const MyWidgetComponent = () => {
 *   useLayoutParamLogger(layoutParams);
 *   // ... rest of component
 * };
 * ```
 *
 * @param layoutParams - The layoutParams object (e.g., from Login/layoutParams.ts)
 */
export const useLayoutParamLogger = (layoutParams: LayoutParamsObject) => {
  const styles = useStyles();

  useParamLogger(layoutParams, styles.get.bind(styles), {
    envVarName: 'LAYOUT_LOGGER_ENABLED',
    logEmoji: '📐',
    paramTypeName: 'layout',
  });
};
